import { ArrowRight, Eye, EyeOff, ShieldCheck, Sparkles } from "lucide-react";
import { useId, useState } from "react";
import type { FormEvent } from "react";
import "../login.css";

export interface LoginPageProps {
  email: string;
  password: string;
  loading?: boolean;
  error?: string | null;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void | Promise<void>;
  onDemo: () => void | Promise<void>;
  onGoogle?: () => void | Promise<void>;
  onForgotPassword?: () => void;
}

export function LoginPage({
  email,
  password,
  loading = false,
  error,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onDemo,
  onGoogle,
  onForgotPassword,
}: LoginPageProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!loading) void onSubmit();
  };

  return (
    <main className="login-page">
      <section className="login-panel" aria-labelledby="login-title">
        <div className="login-panel-inner">
          <a className="login-brand" href="/" aria-label="WeRHungry home">
            <img src="/favicon.png" alt="" />
            <span>WeRHungry</span>
          </a>

          <header className="login-heading">
            <span className="login-eyebrow">KITCHEN OPERATIONS, IN SYNC</span>
            <h1 id="login-title">Welcome back</h1>
            <p>Sign in to see your live kitchen and the next best action.</p>
          </header>

          <form className="login-form" onSubmit={submit} noValidate>
            {onGoogle ? (
              <>
                <button
                  className="google-login"
                  type="button"
                  onClick={() => void onGoogle()}
                  disabled={loading}
                >
                  <GoogleMark />
                  Continue with Google
                </button>
                <div className="login-divider" aria-hidden="true">
                  <span>or continue with email</span>
                </div>
              </>
            ) : null}

            <div className="login-field">
              <label htmlFor={emailId}>Email address</label>
              <input
                id={emailId}
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@restaurant.com"
                value={email}
                onChange={(event) => onEmailChange(event.target.value)}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={Boolean(error)}
                disabled={loading}
                required
              />
            </div>

            <div className="login-field">
              <div className="login-label-row">
                <label htmlFor={passwordId}>Password</label>
                {onForgotPassword ? (
                  <button
                    type="button"
                    className="forgot-password"
                    onClick={onForgotPassword}
                    disabled={loading}
                  >
                    Forgot password?
                  </button>
                ) : null}
              </div>
              <div className="password-input">
                <input
                  id={passwordId}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(event) => onPasswordChange(event.target.value)}
                  aria-describedby={error ? errorId : undefined}
                  aria-invalid={Boolean(error)}
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <label className="remember-control">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                disabled={loading}
              />
              <span>Keep me signed in on this device</span>
            </label>

            {error ? (
              <p className="login-error" id={errorId} role="alert">
                {error}
              </p>
            ) : null}

            <button
              className="login-submit"
              type="submit"
              disabled={loading || !email || !password}
            >
              <span>{loading ? "Signing in…" : "Log in"}</span>
              {!loading ? <ArrowRight size={18} aria-hidden="true" /> : null}
            </button>
          </form>

          <button
            className="demo-access"
            type="button"
            onClick={() => void onDemo()}
            disabled={loading}
          >
            <span className="demo-access-icon" aria-hidden="true">
              <Sparkles size={19} />
            </span>
            <span>
              <strong>Explore the live dinner-shift demo</strong>
              <small>No setup required · Opens Hong Kong Banjeom</small>
            </span>
            <ArrowRight size={18} aria-hidden="true" />
          </button>

          <footer className="login-security">
            <ShieldCheck size={16} aria-hidden="true" />
            <span>Protected by Firebase Authentication</span>
          </footer>
        </div>
      </section>

      <aside className="login-hero" aria-label="WeRHungry live kitchen preview">
        <img src="/images/login-kitchen-hero.png" alt="" />
        <div className="login-hero-shade" />
        <div className="hero-topline">
          <span className="hero-live-dot" />
          Live operational twin
        </div>
        <div className="hero-copy">
          <span>BUILT FOR THE DINNER RUSH</span>
          <h2>See the whole kitchen.<br />Know the next move.</h2>
          <p>
            WeRHungry turns station activity into clear, real-time guidance
            before delays reach the pass.
          </p>
          <div className="hero-signals" aria-label="Live system capabilities">
            <span>Worker guidance</span>
            <span>Predictive prep</span>
            <span>Aisle-aware routing</span>
          </div>
        </div>
      </aside>
    </main>
  );
}

function GoogleMark() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.716v2.259h2.909c1.702-1.567 2.684-3.875 2.684-6.616Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.468-.806 5.956-2.179l-2.909-2.259c-.806.54-1.835.86-3.047.86-2.344 0-4.328-1.585-5.037-3.713H.956v2.332A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.963 10.709A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.281-1.709V4.959H.956A9 9 0 0 0 0 9c0 1.453.348 2.829.956 4.041l3.007-2.332Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.578c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.464.891 11.426 0 9 0A9 9 0 0 0 .956 4.959l3.007 2.332C4.672 5.163 6.656 3.578 9 3.578Z"
      />
    </svg>
  );
}
